// admin-api/lib/padrekko-qc/roleLabels.js
//
// THE ONE PLACE A PADREKKO CASE ROLE BECOMES TEXT A PERSON READS.
//
// ── WHAT WAS WRONG ──────────────────────────────────────────────────────────
//
// FIVE declarations, agreeing pairwise and disagreeing overall:
//
//   ParticipantsModal.jsx      ROLE_LABELS         DH Analyst / Customer Analyst / Operator Analyst
//   QcFindingCard.jsx          PROV_ROLE_LABEL     DH / Customer / Operator (+ AI)
//   CollaborationTimeline.jsx  HANDOFF_ROLE_LABEL  the producer / the owner / the respondent
//   QcPicker.jsx:197           an inline TERNARY, not even a map
//   QcPicker.jsx:672,689       inline `label=` props
//
// plus three server refusal messages in a fourth spelling ("customer analyst",
// lowercase prose) and a stage-keyed map in CaseBanner.jsx.
//
// REM-363 names the shape: this tree guards against copied DECLARATIONS —
// checkStageTablesSingleSource forbids re-declaring a table — and NOT against
// duplicated JUDGEMENTS. Five inline maps that happen to agree is the second
// kind, and it is why the words drifted without anything failing.
//
// CASE_ROLE_LABEL and CASE_ROLE_MEANING are now in that check's NAMES, so a
// sixth declaration under admin-api/ or ui/src fails the commit.
//
// ── WHY NOT caseStages.js, WHICH WAS ALREADY WIRED ─────────────────────────
//
// It says what it is in its own header: THE CASE STAGE TABLES. A role→text map
// is not a stage table, and stageLabels.js exists as a separate file for
// exactly this reason — display mapping is its own concept. Putting this there
// would have cost one line less and made a shared module mean two things, which
// is how the next concept gets folded in for the same reason.
//
// The price of separation is one line in ui/Dockerfile. The compose context
// already points at this whole directory.
//
// ── THE FOUR NAMES FOR ONE ROLE, AND WHERE EACH BELONGS ────────────────────
//
//   STORED         CASE_ROLE_LABEL      CASE_ROLE_SHORT   CASE_ROLE_MEANING
//   dh_analyst     Analysis Provider    Provider          the producer
//   customer_analyst Case Owner         Owner             the owner
//   operator_analyst Assigned Operator  Operator          the respondent
//
// THE STORED ENUM IS UNCHANGED AND MUST STAY THAT WAY. It is the value in
// padrekko_qc_case_participants.role, in the CHECK constraint, in the PK, in
// every audit row's by_role, and in every register row. This file renames
// nothing — it decides what those values LOOK LIKE.
//
// ── CASE_ROLE_MEANING IS NOT AN INTERNAL LAYER. IT ALREADY SHIPS. ──────────
//
// The settled model's producer/owner/respondent vocabulary was already rendered
// to users by CollaborationTimeline's HANDOFF_ROLE_LABEL — "handed off to the
// owner". So this is a SECOND DISPLAY VOCABULARY, not a note about what the
// roles mean, and both live here. Splitting the formal set from the narrative
// set across two files is how they become a sixth and seventh, which is what
// five declarations already demonstrated.
//
// Use CASE_ROLE_LABEL for a header, a picker, a badge or a refusal. Use
// CASE_ROLE_MEANING inside a sentence. Use CASE_ROLE_SHORT only where width is
// genuinely constrained — today that is the per-finding provenance chain.
//
// ── A MESSAGE'S AUDIENCE DECIDES WHICH NAME IT CARRIES ─────────────────────
//
// ┌──────────────────────────────────────────────────────────────────────────┐
// │ A message telling a CALLER WHAT TO SEND names the ENUM.                  │
// │ A message telling a USER WHAT HAPPENED names the LABEL.                  │
// │                                                                          │
// │   "role must be one of: dh_analyst, customer_analyst, operator_analyst"  │
// │        ENUM — it documents the wire contract, and a label there would    │
// │        document a value the API rejects.                                 │
// │                                                                          │
// │   "Only the Case Owner can close the case"                               │
// │        LABEL — it is read by a person, who has never seen the enum.      │
// └──────────────────────────────────────────────────────────────────────────┘
//
// Written down because the inconsistency is VISIBLE and the wrong fix is the
// tempting one: someone tidying these into agreement would put labels in the
// contract messages and break every client that reads them.
//
// ── "ANALYST" MEANS THREE UNRELATED THINGS IN THIS TREE ────────────────────
//
// Whoever greps it lands on all three. This file owns exactly one:
//
//   1. THIS — a PadRekko case-participant role. Three values, per case.
//   2. The WELLFINDER ANALYST — a person adjudicating well records. ~90
//      references across ui/src/components/wellfinder and the workspace.
//   3. The ANALYST LICENSE TIER and the org_analyst org role — see
//      ui/src/constants/licenseTiers.js, orgRoles.js, projectRoles.js. Those
//      carry their own label maps (ORG_ROLE_LABEL, PROJECT_ROLE_LABEL) for a
//      different concept and must NOT be folded in here.
//
// The rename in this file touches (1) only.

/** The formal display name. Headers, pickers, badges, user-facing refusals. */
export const CASE_ROLE_LABEL = Object.freeze({
  dh_analyst:       "Analysis Provider",
  customer_analyst: "Case Owner",
  operator_analyst: "Assigned Operator",
});

/**
 * The narrative form, for use inside a sentence — "handed off to the owner".
 *
 * The settled model's vocabulary, and already user-facing before this file
 * existed. Lower case and article-bearing on purpose: it is a fragment, not a
 * title, and capitalising it would read as a second proper name for the role.
 */
export const CASE_ROLE_MEANING = Object.freeze({
  dh_analyst:       "the producer",
  customer_analyst: "the owner",
  operator_analyst: "the respondent",
});

/**
 * The short form, for genuinely constrained width only.
 *
 * `system` IS A REAL by_role VALUE and not a participant role: the AI autodetect
 * pass stamps it on history entries, so the provenance chain has to render it.
 * It appears here and in neither map above, because it is not a role anybody
 * can hold.
 */
export const CASE_ROLE_SHORT = Object.freeze({
  system:           "AI",
  dh_analyst:       "Provider",
  customer_analyst: "Owner",
  operator_analyst: "Operator",
});

/** Every stored value this file knows how to render. */
export const CASE_ROLES = Object.freeze(Object.keys(CASE_ROLE_LABEL));

/**
 * Label for a role, falling back to the stored value.
 *
 * The fallback is the ENUM rather than "unknown": a screen showing a raw
 * `operator_analyst` is a bug report somebody can act on, and one showing
 * "unknown" is not.
 */
export function roleLabel(role) {
  return CASE_ROLE_LABEL[role] ?? role ?? "";
}

/** Narrative form for a role, or null when there is nothing to say. */
export function roleMeaning(role) {
  return CASE_ROLE_MEANING[role] ?? null;
}
